import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import useAuth from "../../hooks/useAuth";
import DeleteModalComponent from '../../components/DeleteModalComponent';
import EnumService from '../../service/EnumService';
import RecruitmentDataTableUtils from '../../utilities/RecruitmentDataTableUtils';
import RecruitmentCampaignEdit from './RecruitmentCampaignEdit';
import { Calendar } from 'primereact/calendar';
import RecruitmentCampaignView from './RecruitmentCampaignView';
import { RecruitmentService } from '../../service/RecruitmentService';
import { ProgressSpinner } from 'primereact/progressspinner';

const RecruitmentCampaignList = () => {

    const [recruitmentCampaigns, setRecruitmentCampaigns] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    const [recruitmentCampaign, setRecruitmentCampaign] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [viewDialogVisible, setViewDialogVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const disableEdit = false;

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const [filters] = useState({
        'startDate': { value: null, matchMode: FilterMatchMode.DATE_IS },
        'endDate': { value: null, matchMode: FilterMatchMode.DATE_IS },
        'status': { value: null, matchMode: FilterMatchMode.IN }
    });

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        const getEnumValues = async () => {
            const response = await EnumService.getEnumByName("RecruitmentCampaignStatus");
            setStatusValues(response)
        }

        getRecruitmentCampaigns()
        getEnumValues()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getRecruitmentCampaigns = async () => {
        await recruitmentService.getRecruitmentCampaignList({})
            .then((_data) => {
                setRecruitmentCampaigns(_data);
                setIsLoading(false);
            })
            .catch((error) => toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const updateList = async (savedData, isNew) => {
        toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success'), contentClassName: "pcn-toast-success" });
        
        let updatedData = DataTableUtils.updateTableList(recruitmentCampaigns, savedData, isNew)

        setRecruitmentCampaigns(updatedData);
    };

    const showNewRecruitmentCampaignDialog = () => {
        setRecruitmentCampaign(null);
        setEditDialogVisible(true);
    };

    const showEditRecruitmentCampaign = (_recruitmentCampaign) => {
        setRecruitmentCampaign(_recruitmentCampaign);
        setEditDialogVisible(true);
    };

    const showViewDialog = (_recruitmentCampaign) => {
        setRecruitmentCampaign(_recruitmentCampaign);
        setViewDialogVisible(true);
    };

    const showDeleteRecruitmentCampaignDialog = (recruitmentCampaign) => {
        setRecruitmentCampaign(recruitmentCampaign);
        setDeleteDialogVisible(true);
    };

    const hideDeleteRecruitmentCampaignDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteRecruitmentCampaign = () => {
        recruitmentService.deleteRecruitmentCampaign(recruitmentCampaign.id)
            .then(
                () => {
                    let _compaigns = recruitmentCampaigns.filter(item => item.id !== recruitmentCampaign.id)
                    setRecruitmentCampaigns(_compaigns)
                    toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.delete.success'), contentClassName: "pcn-toast-success" });
                },
                (error) => {
                    // console.log(error)
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 7000 });
                }
            ).finally(() => {
                setDeleteDialogVisible(false);
                setRecruitmentCampaign(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {hasPermission('RECRUITMENT_CAMPAIGN_EDIT') && 
                        <Button label={translatedMessage('recruitment.campaign.add')}
                            icon="pi pi-plus"
                            className="p-button-primary mr-2"
                            onClick={showNewRecruitmentCampaignDialog}
                            disabled={disableEdit}
                        />
                    }
                </div>
            </React.Fragment>
        );
    };

    // const handleExport = () => {
    //     let exportData = recruitmentCampaigns.map(item => ({ ...item }));
    //     exportData.map(item => {
    //         let exportItem = item
    //         exportItem.status = translatedMessage("RecruitmentStatus." + item.status)
    //         exportItem.lastUpdatedOn = new Date(Date.parse(item.lastUpdatedOn))
    //         delete exportItem.folderId
    //         delete exportItem.fileUuid
    //         delete exportItem.fileName
    //         delete exportItem.description

    //         return item;
    //     })

    //     return exportData
    // }

    const rightToolbarTemplate = () => {
        return (
            <></>
            // <ExportButtonComponent
            //     getExportData={handleExport}
            //     header={tableHeader}
            //     fileName={translatedMessage("recruitment.campaign")}                    
            // />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {hasPermission('RECRUITMENT_CAMPAIGN_VIEW') &&
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showViewDialog(rowData)}
                        tooltip={translatedMessage("generic.view")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                }
                {hasPermission('RECRUITMENT_CAMPAIGN_EDIT') &&
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditRecruitmentCampaign(rowData)}
                        tooltip={translatedMessage("generic.edit")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} 
                        disabled={disableEdit}/>
                }
                {hasPermission('RECRUITMENT_CAMPAIGN_DELETE') &&
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => showDeleteRecruitmentCampaignDialog(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} 
                        disabled={disableEdit}/>
                }
            </div>
        );
    };

    const calendarRowFilterTemplate = (options) => {
        return <Calendar value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} />
    };


    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className='mb-2'>{translatedMessage('recruitment.campaigns')}</h3>

                            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={recruitmentCampaigns}
                                dataKey="id"
                                paginator
                                rows={DataTableUtils.defalRowsPerPage()}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                responsiveLayout="stack"
                                filterDisplay="row"
                                filters={filters}
                                globalFilterFields={['startDate', 'endDate', 'status']}
                            >
                                <Column
                                    field="startDate"
                                    header={translatedMessage('recruitment.campaign.startDate')}
                                    sortable
                                    headerStyle={{ width: '20%', minWidth: '10rem' }}
                                    body={(e) => GeneralUtils.formatDate(e.startDate)}
                                    filter
                                    showFilterMenu={false}
                                    filterField="startDate"
                                    filterElement={calendarRowFilterTemplate}
                                />
                                <Column
                                    field="endDate"
                                    header={translatedMessage('recruitment.campaign.endDate')}
                                    sortable
                                    headerStyle={{ width: '20%', minWidth: '10rem' }}
                                    body={(e) => GeneralUtils.formatDate(e.endDate)}
                                    filter
                                    showFilterMenu={false}
                                    filterField="endDate"
                                    filterElement={calendarRowFilterTemplate}
                                />
                                <Column
                                    field="status"
                                    header={translatedMessage('generic.status')}
                                    sortable
                                    body={(e) => RecruitmentDataTableUtils.campaignStatusBodyTemplate(e.status)}
                                    headerStyle={{ width: '20%', minWidth: '8rem' }}
                                    filter
                                    filterElement={(e) => RecruitmentDataTableUtils.campaignstatusFilterTemplate(e, statusValues)}
                                    showFilterMenu={false}
                                ></Column>
                                <Column
                                    field="lastModifiedOn"
                                    header={translatedMessage('generic.lastUpdated.on')}
                                    sortable
                                    body={(e) => GeneralUtils.formatDate(e.lastModifiedOn)}
                                    headerStyle={{ width: '10%', minWidth: '8rem' }}
                                ></Column>
                                <Column body={actionBodyTemplate}></Column>
                            </DataTable>

                            {/* Edit dialog */}
                            {editDialogVisible &&
                                <RecruitmentCampaignEdit
                                    value={recruitmentCampaign}
                                    visible={editDialogVisible}
                                    afterSave={updateList}
                                    visibleSetter={setEditDialogVisible}
                                />
                            }

                            {deleteDialogVisible &&
                                <DeleteModalComponent
                                    visible={deleteDialogVisible}
                                    item={recruitmentCampaign?.name}
                                    closeDialog={hideDeleteRecruitmentCampaignDialog}
                                    deleteRecord={() => deleteRecruitmentCampaign()}
                                />
                            }

                            {/* View dialog */}
                            {viewDialogVisible &&
                                <RecruitmentCampaignView
                                    value={recruitmentCampaign}
                                    visible={viewDialogVisible}
                                    visibleSetter={setViewDialogVisible}
                                />
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default RecruitmentCampaignList;
